import { useEffect, useMemo, useRef, useState } from "react";
import { LAYOUT, layoutZone, snapToGrid, type Client, type Stage } from "@board/shared";
import { archPath, rulePath, type Mark as MarkShape } from "./handDrawn";
import { dragThreshold, passedDragThreshold } from "./pointerGesture";
import { CompletedStack, Sticky, StickyDraft } from "./Sticky";
import { useBoard } from "./store";
import { ZoneResizeHandles } from "./ZoneResizeHandle";

/**
 * A client column on the whiteboard. The name is written at the top with its
 * logo beside it, under a drawn rule; below that the four stages are bands
 * stacked down the column, separated by marker strokes, each growing as far
 * down as its notes need. A rule down the right-hand side separates this client
 * from the next.
 *
 * There is no box. The board is one continuous surface and the drawn marks are
 * the only thing dividing it, which is how it works on a real wall.
 *
 * Every sticky's position comes from layoutZone, recomputed on each render.
 * Nothing about a note's place on the board is persisted.
 */

/** Stands in for the note being written, so it takes a real slot in the band. */
const DRAFT_ID = "__draft__";

/** The gap between columns is where the dividing rule is drawn. */
const RULE_OFFSET = 40;

const STATUS_DATE = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});

function statusDate(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? null : STATUS_DATE.format(date);
}

export function Zone({
  client,
  scale,
  visibleTaskIds,
  onFocus,
  onEdit,
  onExpandTask,
  canResize,
  completedExpanded,
  onToggleCompleted,
  dropTarget,
  onTaskDragOver,
  onTaskDrop,
}: {
  client: Client;
  scale: number;
  visibleTaskIds: ReadonlySet<string>;
  onFocus: () => void;
  /** Open this client's details for editing. Admin-only, as the database is. */
  onEdit: () => void;
  /** Double-click on a note: focus this client and open the note in it. */
  onExpandTask: (taskId: string) => void;
  canResize: boolean;
  completedExpanded: boolean;
  onToggleCompleted: () => void;
  dropTarget: { kind: "client" | "worklist"; id: string; stage: Stage } | null;
  onTaskDragOver: (taskId: string, clientX: number, clientY: number) => void;
  onTaskDrop: (taskId: string, clientX: number, clientY: number) => void;
}) {
  const isAdmin = useBoard((s) => s.isAdmin());
  const selected = useBoard((s) => s.selectedClientId === client.id);
  const select = useBoard((s) => s.select);
  const updateClient = useBoard((s) => s.updateClient);
  const allTasks = useBoard((s) => s.tasks);
  const tool = useBoard((s) => s.tool);
  const createTask = useBoard((s) => s.createTask);
  const setTool = useBoard((s) => s.setTool);
  const deleteClient = useBoard((s) => s.deleteClient);

  const [dragging, setDragging] = useState(false);
  const [refused, setRefused] = useState(false);
  const [actionProblem, setActionProblem] = useState("");
  const actions = useRef<HTMLDetailsElement>(null);
  const drag = useRef<{
    pointer: number;
    startX: number;
    startY: number;
    ox: number;
    oy: number;
    onName: boolean;
    canMove: boolean;
    moved: boolean;
    threshold: number;
    dx: number;
    dy: number;
  } | null>(null);
  // Held locally while dragging so the zone tracks the cursor without a write
  // per frame; committed once on release.
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [previewGeometry, setPreviewGeometry] = useState<{
    canvas_x: number; canvas_y: number; width: number; height: number;
  } | null>(null);
  // The note being written, if any. `since` is fixed when the draft opens so
  // the placeholder keeps its slot instead of re-sorting on every keystroke.
  const [draft, setDraft] = useState<{ stage: Stage; since: string } | null>(null);
  // The rule is drawn under the name only, so it has to be as wide as the name
  // actually rendered — which depends on the font and on the client's name, and
  // so can only be measured, not computed.
  const nameEl = useRef<HTMLHeadingElement>(null);
  const [nameWidth, setNameWidth] = useState(0);

  useEffect(() => {
    const el = nameEl.current;
    if (!el) return;
    // offsetWidth, not getBoundingClientRect: the zone sits inside the world
    // transform, so the rect is multiplied by the board scale and the rule
    // would grow and shrink as you zoom.
    const measure = () => setNameWidth(el.offsetWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [client.name]);

  useEffect(() => {
    if (!isAdmin) return;
    const closeActions = (event: Event) => {
      if (event instanceof KeyboardEvent && event.key !== "Escape") return;
      if (event instanceof PointerEvent && actions.current?.contains(event.target as Node)) return;
      actions.current?.removeAttribute("open");
    };
    document.addEventListener("pointerdown", closeActions);
    document.addEventListener("keydown", closeActions);
    return () => {
      document.removeEventListener("pointerdown", closeActions);
      document.removeEventListener("keydown", closeActions);
    };
  }, [isAdmin]);

  // Selecting the whole array keeps the store snapshot stable; the filter has
  // to happen here rather than in the selector, or every render hands React a
  // new array and useSyncExternalStore refuses to settle.
  const tasks = useMemo(
    () => allTasks.filter((t) => t.client_id === client.id && visibleTaskIds.has(t.id)),
    [allTasks, client.id, visibleTaskIds],
  );
  const completedTasks = useMemo(() => tasks.filter((task) => task.stage === "completed"), [tasks]);
  const completedCollapsed = completedTasks.length > 1 && !completedExpanded && draft?.stage !== "completed";
  const width = previewGeometry?.width ?? client.width;

  const layout = useMemo(() => {
    const rows = tasks.map((t) => ({
      id: t.id,
      stage: t.stage,
      due_date: t.due_date,
      created_at: t.created_at,
      priority: t.priority,
    }));
    // The draft is undated and newest, so it lands at the end of its band —
    // which is where the note you are writing belongs.
    if (draft) {
      rows.push({
        id: DRAFT_ID,
        stage: draft.stage,
        due_date: null,
        created_at: draft.since,
        priority: "normal" as const,
      });
    }
    return layoutZone(width, rows, undefined, { collapseCompleted: completedCollapsed });
  }, [tasks, draft, width, completedCollapsed]);

  // Bands grow as far as they need; the stored height is left for auto-tidy to
  // write in Milestone 3 and is not what the column renders at.
  const height = Math.max(previewGeometry?.height ?? client.height, layout.requiredHeight);
  const canvasX = previewGeometry?.canvas_x ?? client.canvas_x;
  const canvasY = previewGeometry?.canvas_y ?? client.canvas_y;
  const placing = tool === "sticky";
  const draftCard = draft ? layout.cards.find((c) => c.taskId === DRAFT_ID) ?? null : null;
  // Drawn under the name and a little past it, the way you underline a word
  // rather than ruling off the whole board. Falls back to a readable minimum
  // before the first measurement lands.
  const nameRule = useMemo(
    () =>
      archPath(Math.max(nameWidth + 22, 90), `${client.id}:name`, { thickness: 6, rise: 5 }),
    [nameWidth, client.id],
  );
  const divider = useMemo(() => rulePath(height, `${client.id}:rule`), [height, client.id]);

  function onPointerDown(event: React.PointerEvent) {
    // Left button only: right-click belongs to the board's context menu.
    if (event.button !== 0) return;
    event.stopPropagation();
    // Stops the header's title being selected as text mid-drag, which is what
    // made the zone stutter and the cursor flicker between grab and I-beam.
    event.preventDefault();
    drag.current = {
      pointer: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      ox: client.canvas_x,
      oy: client.canvas_y,
      // The name is a click target inside what is also the drag handle. Same
      // rule as a sticky: a press that does not move opens, one that moves drags.
      onName: Boolean(
        (event.target as HTMLElement).closest(".zone__name, .zone__logo, .zone__statusblock"),
      ),
      canMove: isAdmin,
      moved: false,
      threshold: dragThreshold(event.pointerType),
      dx: 0,
      dy: 0,
    };
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: React.PointerEvent) {
    const active = drag.current;
    if (!active || active.pointer !== event.pointerId) return;
    if (!active.moved && !passedDragThreshold(
      active.startX, active.startY, event.clientX, event.clientY, active.threshold,
    )) return;
    active.moved = true;
    if (!active.canMove) return;
    active.dx = (event.clientX - active.startX) / scale;
    active.dy = (event.clientY - active.startY) / scale;
    setDragging(true);
    // Screen delta ÷ scale, so a drag tracks the cursor at any zoom level.
    setOffset({
      x: active.dx,
      y: active.dy,
    });
  }

  function onPointerUp(event: React.PointerEvent) {
    const active = drag.current;
    if (!active || active.pointer !== event.pointerId) return;
    drag.current = null;
    setDragging(false);

    if (!active.moved) {
      setOffset({ x: 0, y: 0 });
      select(client.id);
      // A press that went nowhere, on the name: open the client's workspace.
      if (active.onName) onFocus();
      return;
    }
    if (!active.canMove) {
      setOffset({ x: 0, y: 0 });
      return;
    }
    active.dx = (event.clientX - active.startX) / scale;
    active.dy = (event.clientY - active.startY) / scale;

    // Order matters. updateClient writes the new position into the store
    // synchronously before it awaits the server, so applying it *first* and
    // dropping the local offset immediately after means the zone never renders
    // for a frame at its old position with the offset already gone — which is
    // what made a finished drag flash backwards before settling.
    const promise = updateClient(client.id, {
      canvas_x: snapToGrid(active.ox + active.dx),
      canvas_y: snapToGrid(active.oy + active.dy),
    });
    setOffset({ x: 0, y: 0 });

    promise.catch(() => {
      // The store already rolled the position back; say why it snapped home.
      setRefused(true);
      window.setTimeout(() => setRefused(false), 2400);
    });
  }

  function cancelZoneDrag(event: React.PointerEvent) {
    if (drag.current?.pointer !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
    setOffset({ x: 0, y: 0 });
  }

  function place(stage: Stage) {
    select(client.id);
    setDraft({ stage, since: new Date().toISOString() });
  }

  function commit(title: string) {
    const stage = draft?.stage;
    setDraft(null);
    if (!stage) return;
    // The pad goes back on the rail. It used to stay in hand so the next note
    // was one click away, but while it is out every note on the board is
    // click-through — a click has to be able to reach the section underneath so
    // a full section can still be added to. The cost was that a note you had
    // just written could not be opened, which is the first thing you want to do
    // with it. Writing a batch is now one extra click per note; opening the note
    // you just wrote is no clicks at all.
    setTool("select");
    createTask({ client_id: client.id, title, stage }).catch(() => {
      setRefused(true);
      window.setTimeout(() => setRefused(false), 2400);
    });
  }

  async function moveToRecycleBin() {
    actions.current?.removeAttribute("open");
    if (!window.confirm(`Move “${client.name}” and everything on its board to the recycle bin?`)) return;
    setActionProblem("");
    try {
      await deleteClient(client.id);
    } catch {
      setActionProblem("That board couldn’t be moved to the recycle bin.");
      window.setTimeout(() => setActionProblem(""), 3200);
    }
  }

  return (
    <section
      className={`zone${selected ? " zone--selected" : ""}${dragging ? " zone--dragging" : ""}`}
      style={{
        left: canvasX + offset.x,
        top: canvasY + offset.y,
        width,
        height,
      }}
      aria-label={client.name}
    >
      <header
        className={`zone__header${isAdmin ? " zone__header--draggable" : ""}`}
        style={{ height: LAYOUT.zone.headerHeight, padding: `0 ${LAYOUT.zone.padding}px` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={cancelZoneDrag}
      >
        <h2 className="zone__name" ref={nameEl} style={{ color: client.name_color }}>
          {client.name}
        </h2>
        {client.logo_updated_at ? (
          <img
            className="zone__logo"
            // The timestamp is the cache key: the bytes are fetched once and
            // then reused until the logo actually changes.
            src={`/api/clients/${client.id}/logo?v=${encodeURIComponent(client.logo_updated_at)}`}
            alt=""
          />
        ) : null}
        {/* Where the relationship stands, right beside the logo — written on
            the board in marker, because that is what it is. */}
        {client.status ? (
          <span className="zone__statusblock">
            <span className="zone__status">{client.status}</span>
            {statusDate(client.status_updated_at) ? (
              <time className="zone__statusdate" dateTime={client.status_updated_at ?? undefined}>
                since {statusDate(client.status_updated_at)}
              </time>
            ) : null}
          </span>
        ) : null}
        {isAdmin ? (
          <details className="zone__actions" ref={actions}
            onPointerDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}>
            <summary aria-label={`Actions for ${client.name}`} title="Client actions">•••</summary>
            <div className="zone__actionmenu" role="menu">
              <button type="button" role="menuitem" onClick={() => {
                actions.current?.removeAttribute("open");
                onEdit();
              }}>Edit board</button>
              <button type="button" role="menuitem" className="zone__actiondanger"
                onClick={() => void moveToRecycleBin()}>Move to recycle bin</button>
            </div>
          </details>
        ) : null}
        {refused ? <span className="zone__refused">That change didn’t save</span> : null}
        {actionProblem ? <span className="zone__refused" role="alert">{actionProblem}</span> : null}
      </header>

      {/* The rule under the client name. */}
      <Mark mark={nameRule} className="mark--name" left={LAYOUT.zone.padding} top={LAYOUT.zone.headerHeight - 8} />

      <div className={`zone__field${placing ? " zone__field--placing" : ""}`}>
        {layout.bands.map((band) => (
          <div
            key={band.stage}
            className={
              `band band--${band.stage.replace("_", "-")}` +
              (placing ? " band--placing" : "") +
              (dropTarget?.kind === "client" && dropTarget.id === client.id && dropTarget.stage === band.stage ? " band--drop" : "")
            }
            style={{ left: band.x, top: band.y, width: band.width, height: band.height }}
            data-task-drop="client"
            data-drop-id={client.id}
            data-drop-stage={band.stage}
            onPointerDown={(event) => {
              if (event.button !== 0 || !placing) return;
              event.stopPropagation();
              event.preventDefault(); // no text selection while placing
              place(band.stage);
            }}
            // The mousedown that follows pointerdown is what moves focus, and
            // preventDefault on a pointer event does not suppress it. Left
            // alone it focuses the body, which blurs the note's field the frame
            // it mounts — and an empty blur discards the draft, so the note
            // silently never appears.
            onMouseDown={(event) => {
              if (placing) event.preventDefault();
            }}
            // Double-clicking blank space in a section writes a note there,
            // without going to the rail for the pad first. The pad is still the
            // way to write several in a row; this is the way to write one.
            //
            // It only fires on the band itself — event.target is the check that
            // a note, or anything else sitting in the band, was not what was
            // double-clicked. A note has its own double-click, and the two must
            // not both go off.
            onDoubleClick={(event) => {
              if (placing || tool !== "select") return;
              if (event.target !== event.currentTarget) return;
              place(band.stage);
            }}
          />
        ))}

        {layout.cards.map((card) => {
          if (card.taskId === DRAFT_ID) return null;
          const task = tasks.find((t) => t.id === card.taskId);
          if (task && card.stage === "completed" && completedCollapsed) {
            return <CompletedStack key="completed-stack" task={task} at={card}
              count={completedTasks.length} onExpand={onToggleCompleted} />;
          }
          return task ? (
            <Sticky
              key={task.id}
              task={task}
              at={card}
              scale={scale}
              onDragOver={(x, y) => onTaskDragOver(task.id, x, y)}
              onDrop={onTaskDrop}
              onExpand={onExpandTask}
            />
          ) : null;
        })}

        {draft && draftCard ? (
          <StickyDraft
            stage={draft.stage}
            at={draftCard}
            onCommit={commit}
            onCancel={() => setDraft(null)}
          />
        ) : null}
        {completedExpanded && completedTasks.length > 1 ? (
          <button type="button" className="completed-stack__collapse"
            style={{
              left: width - LAYOUT.zone.padding - 132,
              top: (layout.bands.find((band) => band.stage === "completed")?.y ?? 0) - 38,
            }}
            aria-expanded="true"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => { event.stopPropagation(); onToggleCompleted(); }}>
            Stack {completedTasks.length} completed
          </button>
        ) : null}
      </div>

      {/* The stroke between each pair of stages. */}
      {layout.separators.map((y, i) => (
        <Separator
          key={i}
          y={y}
          width={width - LAYOUT.zone.padding * 2}
          seed={`${client.id}:sep:${i}`}
        />
      ))}

      {/* The rule down the side, dividing this client from the next. */}
      {isAdmin && canResize ? <ZoneResizeHandles label={client.name}
        geometry={{ canvas_x: canvasX, canvas_y: canvasY, width, height }}
        minHeight={layout.requiredHeight} scale={scale}
        onPreview={setPreviewGeometry} onCommit={(next) => updateClient(client.id, next)}
        onProblem={() => {
          setRefused(true);
          window.setTimeout(() => setRefused(false), 2400);
        }} /> : null}

      <Mark mark={divider} className="mark--rule" left={width + RULE_OFFSET} top={0} />
    </section>
  );
}

function Separator({ y, width, seed }: { y: number; width: number; seed: string }) {
  const mark = useMemo(() => archPath(width, seed), [width, seed]);
  return <Mark mark={mark} className="mark--separator" left={LAYOUT.zone.padding} top={y} />;
}

/**
 * One drawn mark, placed at its natural size. `left` and `top` are where the
 * mark's own origin goes — for a horizontal stroke that is its baseline, so the
 * box's negative y is what lifts the ink above the line it was drawn along.
 */
function Mark({
  mark,
  className,
  left,
  top,
}: {
  mark: MarkShape;
  className: string;
  left: number;
  top: number;
}) {
  return (
    <svg
      className={`mark ${className}`}
      style={{ left: left + mark.x, top: top + mark.y }}
      width={mark.width}
      height={mark.height}
      viewBox={mark.viewBox}
      aria-hidden="true"
    >
      <path d={mark.d} fill="var(--ink-marker)" />
    </svg>
  );
}
